import React from "react";
import { diffArrays } from "diff";
import { Button, Group, Loader, Modal } from "@mantine/core";

const BLOCK_STYLES = {
	latest: {
		label: "Only in the latest saved version",
		style: {
			background: "var(--mantine-color-red-light)",
			borderLeft: "4px solid var(--mantine-color-red-filled)",
		},
	},
	yours: {
		label: "Only in your version",
		style: {
			background: "var(--mantine-color-green-light)",
			borderLeft: "4px solid var(--mantine-color-green-filled)",
		},
	},
	same: {
		label: null,
		style: { borderLeft: "4px solid transparent", opacity: 0.6 },
	},
};

// Splits note HTML into its top level blocks (paragraphs, lists, headings...).
// Blocks are matched on a key with attributes stripped (except href) so the
// server's sanitizing of things like link attributes doesn't show as a change
function splitIntoBlocks(html) {
	const body = new DOMParser().parseFromString(html || "", "text/html").body;
	return Array.from(body.children).map((element) => {
		const keyElement = element.cloneNode(true);
		[keyElement, ...keyElement.querySelectorAll("*")].forEach((node) => {
			Array.from(node.attributes)
				.filter((attribute) => attribute.name !== "href")
				.forEach((attribute) => node.removeAttribute(attribute.name));
		});
		return { html: element.outerHTML, key: keyElement.outerHTML };
	});
}

function DiffGroup({ kind, blocks }) {
	const { label, style } = BLOCK_STYLES[kind];
	return (
		<div style={{ ...style, padding: "4px 12px", marginBottom: 8 }}>
			{label ? (
				<div
					style={{
						fontSize: 12,
						fontWeight: 600,
						margin: "4px 0",
					}}
				>
					{label}
				</div>
			) : null}
			{blocks.map((block, index) => (
				<div
					key={index}
					className="NoteHtmlContent"
					// both versions are note HTML (sanitized by the server on save)
					dangerouslySetInnerHTML={{ __html: block.html }}
				/>
			))}
		</div>
	);
}

// Shows how the user's unsaved note differs from the latest saved version,
// block by block, so they can copy what they want before loading it
export default function NoteCompareModal({
	opened,
	onClose,
	latestVersion,
	yourHtml,
	onLoadLatest,
}) {
	let body;
	if (!latestVersion) {
		body = (
			<Group justify="center" p="xl">
				<Loader />
			</Group>
		);
	} else if (latestVersion.error) {
		body = (
			<div style={{ color: "var(--mantine-color-error)" }}>
				The latest version of this note could not be loaded.
			</div>
		);
	} else {
		const parts = diffArrays(
			splitIntoBlocks(latestVersion.html),
			splitIntoBlocks(yourHtml),
			{ comparator: (left, right) => left.key === right.key }
		);
		const hasDifferences = parts.some((part) => part.added || part.removed);
		body = hasDifferences ? (
			parts.map((part, index) => (
				<DiffGroup
					key={index}
					kind={
						part.removed ? "latest" : part.added ? "yours" : "same"
					}
					blocks={part.value}
				/>
			))
		) : (
			<div>
				Your version matches the latest saved version, so nothing will
				be lost by loading it.
			</div>
		);
	}

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title="Compare with the latest saved version"
			size="xl"
		>
			{body}
			<Group justify="flex-end" mt="md">
				<Button variant="default" onClick={onClose}>
					Close
				</Button>
				<Button color="red" onClick={onLoadLatest}>
					Load latest version
				</Button>
			</Group>
		</Modal>
	);
}
